import { useCallback, useEffect, useState } from "react";
import { Plus, FileText } from "lucide-react";
import { api, today } from "@/lib/api";
import { useAuth, puedeEco } from "@/lib/auth";
import { useCentro } from "@/lib/centro";
import { PageTitle } from "@/components/Badges";
import { Button } from "@/components/ui/button";
import { CobroDialog } from "@/components/eco/CobroDialog";
import { FacturaDialog } from "@/components/eco/FacturaDialog";
import { CajaDia, TablaCobros, Pendientes } from "@/components/eco/EcoTablas";
import { Facturas, ExportGestoria } from "@/components/eco/Facturas";

const TABS = [["caja", "Caja del día"], ["cobros", "Cobros"], ["pendientes", "Pendiente por paciente"], ["facturas", "Facturas"]];
const trimestre = () => {
  const d = new Date();
  const q = Math.floor(d.getMonth() / 3) * 3;
  const pad = (n) => String(n).padStart(2, "0");
  const fin = new Date(d.getFullYear(), q + 3, 0).getDate();
  return [`${d.getFullYear()}-${pad(q + 1)}-01`, `${d.getFullYear()}-${pad(q + 3)}-${pad(fin)}`];
};

const Fechas = ({ desde, hasta, setDesde, setHasta, solo }) => (
  <div className="flex items-center gap-2 text-xs text-slate-600">
    {solo ? "Día" : "Desde"} <input type="date" value={desde} onChange={(e) => { setDesde(e.target.value); if (solo) setHasta(e.target.value); }} data-testid="eco-desde" className="h-8 rounded border border-slate-200 px-2" />
    {!solo && <>hasta <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} data-testid="eco-hasta" className="h-8 rounded border border-slate-200 px-2" /></>}
  </div>
);

export default function Economico() {
  const { user } = useAuth();
  const { centro } = useCentro();
  const [tab, setTab] = useState("caja");
  const [[qd, qh]] = useState(trimestre);
  const [desde, setDesde] = useState(today());
  const [hasta, setHasta] = useState(today());
  const [cobros, setCobros] = useState([]);
  const [dlg, setDlg] = useState(null);
  const load = useCallback(() => {
    api.get("/eco/cobros", { params: { desde, hasta, centro } }).then((r) => setCobros(r.data));
  }, [desde, hasta, centro]);
  useEffect(() => {
    if (puedeEco(user)) load();
  }, [load, user]);
  if (!puedeEco(user)) return <p className="text-sm text-slate-500">No tienes acceso a la parte económica. Pídeselo a una administradora.</p>;
  const cambiar = (t) => {
    setTab(t);
    if (t === "caja") { setDesde(today()); setHasta(today()); } else if (t !== "pendientes" && desde === hasta) { setDesde(qd); setHasta(qh); }
  };
  return (
    <div data-testid="economico-page">
      <PageTitle title="Económico" subtitle="Solo lo ven las administradoras y las personas con permiso. Usa el selector de centro de arriba para filtrar." />
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => cambiar(k)} data-testid={`eco-tab-${k}`}
            className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${tab === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>{l}</button>
        ))}
        <div className="flex-1" />
        <Button size="sm" onClick={() => setDlg("cobro")} data-testid="eco-nuevo-cobro" className="bg-[#1E3A8A] hover:bg-[#1E40AF]"><Plus className="h-4 w-4 mr-1" />Apuntar cobro</Button>
        <Button size="sm" variant="outline" onClick={() => setDlg("factura")} data-testid="eco-nueva-factura"><FileText className="h-4 w-4 mr-1" />Nueva factura</Button>
      </div>
      {tab !== "pendientes" && (
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <Fechas desde={desde} hasta={hasta} setDesde={setDesde} setHasta={setHasta} solo={tab === "caja"} />
          {tab !== "caja" && <ExportGestoria desde={desde} hasta={hasta} />}
        </div>
      )}
      {tab === "caja" && <div className="space-y-4"><CajaDia cobros={cobros} /><TablaCobros cobros={cobros} onChange={load} /></div>}
      {tab === "cobros" && <TablaCobros cobros={cobros} onChange={load} />}
      {tab === "pendientes" && <Pendientes centro={centro} />}
      {tab === "facturas" && <Facturas desde={desde} hasta={hasta} />}
      <CobroDialog open={dlg === "cobro"} onClose={() => setDlg(null)} onDone={() => { setDlg(null); load(); }} />
      <FacturaDialog open={dlg === "factura"} onClose={() => setDlg(null)} />
    </div>
  );
}
